import { Button, buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { AlertCircle, RefreshCcw } from 'lucide-react'
import { memo, type ReactNode } from 'react'

type ErrorAlertProps = {
  title?: string
  message: JSX.Element | string
  className?: string
  onRetry?: () => void
  action?: ReactNode
}

export const ErrorAlert = memo(function ErrorAlert({
  title = 'Error',
  message,
  className,
  onRetry,
  action,
}: ErrorAlertProps) {
  return (
    <div className={cn('flex h-full items-center justify-center p-4', className)}>
      <div className="w-full max-w-lg rounded-xl bg-card p-6 shadow-lg ring-1 ring-foreground/10">
        <div className="mb-6 flex items-center justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-6! w-6! text-destructive" />
          </div>
        </div>

        <div className="text-center">
          <img src="/daisy_logomark_sunrise.png" alt="Daisy" className="mx-auto mb-4 h-7 w-auto" />
          <div className="mb-2 text-xl font-medium text-foreground">{title}</div>
          <p className="mb-6 whitespace-pre-line text-muted-foreground">{message}</p>

          <div className="flex justify-center gap-3">
            {onRetry ? (
              <Button onClick={onRetry} className="inline-flex items-center gap-2">
                <RefreshCcw className="h-4 w-4" />
                Try again
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2"
              >
                Refresh page
              </Button>
            )}
            {action}
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground">
            If this issue persists, please contact support at{' '}
            <a
              href={`mailto:bloomintegration@daisyco.com?subject=${encodeURIComponent(
                `Proposal support request: ${title}`
              )}&body=${encodeURIComponent(
                `Hi, I'm running into an issue with the Daisy proposal tool. Here are the error details:\n\nTitle: ${title}\nMessage: ${typeof message === 'string' ? message : title}\n\nAdditional information:\n- URL: ${window.location.href}\n- Time: ${new Date().toISOString()}`
              )}`}
              className={cn(buttonVariants({ variant: 'link', className: 'text-xs' }))}
            >
              bloomintegration@daisyco.com
            </a>
          </p>
        </div>
      </div>
    </div>
  )
})
